import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar, Clock, Phone, Droplet, User, ChevronRight,
  Pill, FlaskConical, CreditCard, Zap, CalendarCheck, CalendarClock, Wallet,
  FileText, Printer, AlertCircle, MapPin, CheckCircle2, X
} from "lucide-react";

import PatientAvatar from "@/assets/avatars/patient.jpg";
import DrPriyaAvatar from "@/assets/avatars/dr_priya.jpg";
import DrAmitAvatar from "@/assets/avatars/dr_amit.jpg";
import DrNehaAvatar from "@/assets/avatars/dr_neha.jpg";

const getDoctorAvatar = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("priya") || n.includes("sharma")) return DrPriyaAvatar;
  if (n.includes("amit") || n.includes("smith") || n.includes("deep") || n.includes("kusagradhi")) return DrAmitAvatar;
  if (n.includes("neha") || n.includes("sarah")) return DrNehaAvatar;
  return DrPriyaAvatar;
};

function BillingTabContent({ unpaidBills, paidBills, payingBill, payBill, downloadInvoice, totalSpent, navigate }) {
  const [sub, setSub] = useState("unpaid");
  const list = sub === "unpaid" ? unpaidBills : paidBills;
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-slate-900 font-sans">Billing &amp; Payments</h2>
        <span className="text-xs text-slate-400">Total Billed: ₹{totalSpent.toLocaleString()}</span>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 w-fit">
        {["unpaid", "paid"].map((t) => (
          <button
            key={t}
            onClick={() => setSub(t)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition capitalize ${
              sub === t ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t === "unpaid" ? `Unpaid (${unpaidBills.length})` : `Paid (${paidBills.length})`}
          </button>
        ))}
      </div>

      {list.length > 0 ? (
        <div className="space-y-3">
          {list.map((bill) => (
            <div
              key={bill.id}
              className="rounded-xl border border-slate-200/80 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-emerald-300 transition-colors"
            >
              <div className="flex items-center gap-3.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  bill.status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                }`}>
                  <CreditCard size={18} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-slate-700">{bill.bill_number || bill.id?.slice(-8)}</span>
                    <span className="text-xs text-slate-400">• {bill.created_at?.split("T")[0]}</span>
                  </div>
                  <p className="text-base font-bold text-slate-900 mt-0.5">₹{(bill.patient_payable ?? bill.total_amount ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500 capitalize">{bill.doctor_name || "Consultation"}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold capitalize ${
                  bill.status === "paid"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                    : bill.status === "pending"
                    ? "bg-blue-50 text-blue-700 border border-blue-100"
                    : "bg-amber-50 text-amber-700 border border-amber-100"
                }`}>
                  {bill.status}
                </span>

                {bill.status !== "paid" && (
                  <Button
                    size="sm"
                    disabled={payingBill === bill.id}
                    onClick={() => payBill(bill)}
                    className="text-xs bg-[#0c7a5f] hover:bg-[#0a664e] text-white rounded-lg gap-1.5"
                  >
                    <Wallet size={13} />
                    {payingBill === bill.id ? "Opening..." : "Pay Now"}
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadInvoice(bill)}
                  className="text-xs font-medium text-slate-700 border-slate-200 hover:text-[#0c7a5f] rounded-lg gap-1.5"
                >
                  <FileText size={13} /> {bill.status === "paid" ? "Download Invoice" : "View Invoice"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-slate-400">
          <CreditCard size={40} className="mx-auto mb-2 opacity-30 text-amber-700" />
          <p className="text-sm">{sub === "unpaid" ? "No pending payments" : "No paid invoices yet"}</p>
          {sub === "unpaid" && (
            <p className="text-xs mt-1 text-slate-400">Bills appear here when your consultation starts</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function PatientPortal() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [labReports, setLabReports] = useState([]);
  const [bills, setBills] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active tab state (appointments, prescriptions, lab, bills)
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "appointments");

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl === "lab-reports" ? "lab" : tabFromUrl === "billing" ? "bills" : tabFromUrl);
    }
  }, [tabFromUrl]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    const urlTab = tabKey === "lab" ? "lab-reports" : tabKey === "bills" ? "billing" : tabKey;
    setSearchParams(urlTab === "appointments" ? {} : { tab: urlTab });
  };

  // Modals state
  const [viewApptDetails, setViewApptDetails] = useState(null);
  const [viewPrescription, setViewPrescription] = useState(null);
  const [viewBill, setViewBill] = useState(null);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const fetchPortalData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, l, b, rx] = await Promise.all([
        api.get("/portal/me"),
        api.get("/portal/appointments"),
        api.get("/portal/lab-reports"),
        api.get("/portal/bills"),
        api.get("/portal/prescriptions"),
      ]);
      setProfile(p.data);
      setAppointments(Array.isArray(a.data?.data) ? a.data.data : (Array.isArray(a.data) ? a.data : []));
      setLabReports(Array.isArray(l.data) ? l.data : (l.data?.data || []));
      setBills(Array.isArray(b.data) ? b.data : (b.data?.data || []));
      setPrescriptions(Array.isArray(rx.data) ? rx.data : (rx.data?.data || []));
    } catch (err) {
      console.error(err);
      toast({ title: "Unable to load portal data", description: err.message || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  useEffect(() => {
    if (!rescheduleAppt || !rescheduleDate) return;

    let active = true;
    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const { data } = await api.get(`/doctors/${rescheduleAppt.doctor_id}/available-slots?date=${rescheduleDate}`);
        if (active) {
          setAvailableSlots(data);
          setSelectedSlot((prev) => prev || data?.slots?.[0]?.start || "");
        }
      } catch (err) {
        if (active) {
          toast({ title: "Unable to load slots", description: formatError(err), variant: "destructive" });
          setAvailableSlots(null);
        }
      } finally {
        if (active) setLoadingSlots(false);
      }
    };

    fetchSlots();
    return () => {
      active = false;
    };
  }, [rescheduleAppt, rescheduleDate, toast]);

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
    try {
      await api.post(`/portal/appointment/cancel/${appointmentId}`);
      toast({ title: "Appointment cancelled successfully" });
      setViewApptDetails(null);
      fetchPortalData();
    } catch (err) {
      toast({ title: "Cancellation failed", description: formatError(err), variant: "destructive" });
    }
  };

  const openReschedule = (appt) => {
    setViewApptDetails(null);
    setRescheduleAppt(appt);
    setRescheduleDate(appt.visit_date?.split("T")[0] || new Date().toISOString().split("T")[0]);
    setSelectedSlot(appt.slot_time || "");
  };

  const handleReschedule = async () => {
    if (!rescheduleAppt || !selectedSlot || !rescheduleDate) return;
    setRescheduleSaving(true);
    try {
      await api.put(`/portal/appointment/edit/${rescheduleAppt.id}`, {
        visit_date: rescheduleDate,
        slot_time: selectedSlot,
      });
      toast({ title: "Appointment rescheduled successfully" });
      setRescheduleAppt(null);
      fetchPortalData();
    } catch (err) {
      toast({ title: "Reschedule failed", description: formatError(err), variant: "destructive" });
    } finally {
      setRescheduleSaving(false);
    }
  };

  const upcomingAppts = appointments.filter((a) => a.status === "waiting" || a.status === "in_consultation" || a.status === "upcoming");
  const pastAppts = appointments.filter((a) => a.status === "completed" || a.status === "cancelled");
  const totalSpent = bills.reduce((s, b) => s + (b.total_amount || 0), 0);

  // Group all appointments by Month & Year for timeline
  const groupedAppointments = (() => {
    const groups = {};
    appointments.forEach((a) => {
      const dt = a.visit_date?.split("T")[0] || "2026-09-07";
      const [y, m] = dt.split("-");
      const key = `${y}-${m}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    });
    // Sort keys descending
    const sortedKeys = Object.keys(groups).sort().reverse();
    return sortedKeys.map((k) => {
      const d = new Date(`${k}-01T00:00:00`);
      const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
      return { key: k, label, list: groups[k] };
    });
  })();

  const nextUpcoming = upcomingAppts[0] || appointments[0];

  const downloadInvoice = (bill) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/bills/${bill.id}/pdf`, "_blank");
  };

  const [payingBill, setPayingBill] = useState(null);

  const payBill = async (bill) => {
    setPayingBill(bill.id);
    try {
      // 1. Load Razorpay checkout.js if not already loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve;
          s.onerror = reject;
          document.body.appendChild(s);
        });
      }

      // 2. Create Razorpay order
      const { data } = await api.post("/payments/create-order", { bill_id: bill.id });
      const order = data.data;

      // 3. Open Razorpay checkout
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount_in_paise,
          currency: order.currency || "INR",
          name: order.business_name || "MedLif Clinic",
          description: order.description || `Invoice ${bill.bill_number}`,
          order_id: order.razorpay_order_id,
          prefill: { name: user?.name || "", email: user?.email || "" },
          theme: { color: "#0c7a5f" },
          handler: async (response) => {
            try {
              await api.post("/payments/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast({ title: "Payment successful!", description: "Your invoice is now paid." });
              fetchPortalData();
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: { ondismiss: () => resolve() },
        });
        rzp.open();
      });
    } catch (err) {
      toast({ title: "Payment failed", description: formatError(err), variant: "destructive" });
    } finally {
      setPayingBill(null);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#0c7a5f] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium">Loading your patient dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* 1. TOP HERO PROFILE BANNER (Matching Image 2) */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-[#0d6452] via-[#0f7663] to-[#095543] p-6 text-white shadow-sm">
        {/* Background Subtle Watermark Graphics */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none opacity-10 select-none">
          <svg width="220" height="220" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 2v20M2 12h20" />
          </svg>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          {/* Left: Avatar & Patient Vitals */}
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full ring-4 ring-white/30 overflow-hidden flex-shrink-0 bg-emerald-950 shadow-md">
              <img
                src={PatientAvatar}
                alt="Demo Patient"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <p className="text-emerald-100/90 text-xs font-normal">Hello,</p>
              <h1 className="text-2xl font-bold text-white tracking-tight font-sans">
                {user?.name || "Demo Patient"}
              </h1>

              {/* Meta tags matching Image 2 */}
              <div className="flex flex-wrap items-center gap-2.5 mt-2.5">
                <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-xs px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-50 border border-white/10">
                  <Calendar size={13} className="text-emerald-200" />
                  <span className="text-emerald-200/80 text-[11px]">Patient ID</span>
                  <span className="font-semibold">{profile?.patient_id || "P0001"}</span>
                </div>

                <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-xs px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-50 border border-white/10">
                  <Droplet size={13} className="text-emerald-200" />
                  <span className="text-emerald-200/80 text-[11px]">Blood Group</span>
                  <span className="font-semibold">{profile?.blood_group || "O+"}</span>
                </div>

                <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-xs px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-50 border border-white/10">
                  <User size={13} className="text-emerald-200" />
                  <span className="text-emerald-200/80 text-[11px]">Age</span>
                  <span className="font-semibold">{profile?.age ? `${profile.age} yrs` : "32 yrs"}</span>
                </div>

                <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-xs px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-50 border border-white/10">
                  <Phone size={13} className="text-emerald-200" />
                  <span className="text-emerald-200/80 text-[11px]">Emergency Contact</span>
                  <span className="font-semibold">{profile?.emergency_contact || profile?.phone || "+91 98765 43210"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Stay Healthy Stay Happy Cursive Art Note */}
          <div className="hidden lg:flex flex-col items-end pr-4 select-none">
            <div className="flex items-center gap-2">
              <span className="text-lg font-serif italic text-emerald-50 tracking-wide opacity-95">
                Stay Healthy
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-serif italic text-emerald-50 tracking-wide opacity-95">
                Stay Happy
              </span>
              <svg className="w-5 h-5 text-emerald-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* 2. STAT METRICS (4 Cards in a Row, matching Image 2) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Visits */}
        <div
          onClick={() => handleTabChange("appointments")}
          className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center justify-between cursor-pointer hover:border-emerald-300 hover:shadow-xs transition duration-150 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
              <CalendarCheck size={22} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Visits</p>
              <p className="text-2xl font-bold text-slate-900 leading-tight font-sans mt-0.5">
                {appointments.length || "2"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Last 6 months</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-300 group-hover:text-emerald-700 transition" />
        </div>

        {/* Upcoming Appointments */}
        <div
          onClick={() => handleTabChange("appointments")}
          className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center justify-between cursor-pointer hover:border-blue-300 hover:shadow-xs transition duration-150 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#eef6ff] text-[#2563eb] flex items-center justify-center flex-shrink-0">
              <CalendarClock size={22} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Upcoming Appointments</p>
              <p className="text-2xl font-bold text-slate-900 leading-tight font-sans mt-0.5">
                {upcomingAppts.length || "1"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Next 7 days</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-300 group-hover:text-blue-600 transition" />
        </div>

        {/* Lab Reports */}
        <div
          onClick={() => handleTabChange("lab")}
          className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center justify-between cursor-pointer hover:border-purple-300 hover:shadow-xs transition duration-150 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#f5f3ff] text-[#7c3aed] flex items-center justify-center flex-shrink-0">
              <FlaskConical size={22} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Lab Reports</p>
              <p className="text-2xl font-bold text-slate-900 leading-tight font-sans mt-0.5">
                {labReports.length || "2"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Available</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-300 group-hover:text-purple-600 transition" />
        </div>

        {/* Total Billed */}
        <div
          onClick={() => handleTabChange("bills")}
          className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center justify-between cursor-pointer hover:border-amber-300 hover:shadow-xs transition duration-150 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#fff7ed] text-[#ea580c] flex items-center justify-center flex-shrink-0">
              <Wallet size={22} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Billed</p>
              <p className="text-2xl font-bold text-[#ea580c] leading-tight font-sans mt-0.5">
                ₹{totalSpent > 0 ? totalSpent.toLocaleString() : "900"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Last 6 months</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-300 group-hover:text-amber-600 transition" />
        </div>
      </div>

      {/* 3. MAIN 2-COLUMN SECTION (Matching Image 2) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT COLUMN: Tabbed Content (Appointments, Prescriptions, Lab Reports, Billing) */}
        <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-xs flex flex-col justify-between">
          <div>
            {/* Clean Tab Navigation Bar */}
            <div className="flex items-center border-b border-slate-100 px-6 pt-2 gap-8 overflow-x-auto">
              {[
                { id: "appointments", label: "Appointments", icon: CalendarCheck },
                { id: "prescriptions", label: "Prescriptions", icon: Pill },
                { id: "lab", label: "Lab Reports", icon: FlaskConical },
                { id: "bills", label: "Billing", icon: CreditCard },
              ].map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-2 py-3.5 text-sm font-semibold transition-all border-b-2 -mb-px whitespace-nowrap ${
                      active
                        ? "border-[#0c7a5f] text-[#0c7a5f]"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    <Icon size={16} className={active ? "text-[#0c7a5f]" : "text-slate-400"} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* TAB CONTENT: APPOINTMENTS */}
            {activeTab === "appointments" && (
              <div>
                <div className="px-6 py-4 border-b border-slate-100/80">
                  <h2 className="text-sm font-bold text-slate-900 font-sans">
                    Upcoming & Past Appointments
                  </h2>
                </div>

                {groupedAppointments.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {groupedAppointments.map((group) => (
                      <div key={group.key}>
                        {/* Month Header Banner */}
                        <div className="px-6 py-2 bg-slate-50/70 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100/60">
                          {group.label}
                        </div>

                        {/* List of Appointments in this Month */}
                        <div className="divide-y divide-slate-50">
                          {group.list.map((appt) => {
                            const dateStr = appt.visit_date?.split("T")[0] || "2026-09-07";
                            const d = new Date(dateStr);
                            const dayNum = String(d.getDate()).padStart(2, "0");
                            const monthShort = d.toLocaleString("en-US", { month: "short" });
                            const dayName = d.toLocaleString("en-US", { weekday: "short" });
                            const isUpcoming = appt.status === "waiting" || appt.status === "in_consultation" || appt.status === "upcoming";

                            return (
                              <div
                                key={appt.id}
                                className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 hover:bg-slate-50/60 transition"
                              >
                                <div className="flex items-center gap-4">
                                  {/* Date Badge */}
                                  <div className="w-14 h-14 min-w-[56px] rounded-xl bg-[#f0f7f4] border border-emerald-100 flex flex-col items-center justify-center text-center flex-shrink-0">
                                    <span className="text-base font-bold text-[#0c7a5f] leading-none">{dayNum}</span>
                                    <span className="text-[10px] font-semibold text-slate-600 uppercase mt-0.5">{monthShort}</span>
                                    <span className="text-[9px] text-slate-400">{dayName}</span>
                                  </div>

                                  {/* Timeline Node connector dot */}
                                  <div className="hidden sm:flex flex-col items-center justify-center">
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#0c7a5f]" />
                                  </div>

                                  {/* Doctor Avatar */}
                                  <img
                                    src={getDoctorAvatar(appt.doctor_name)}
                                    alt={appt.doctor_name}
                                    className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-xs flex-shrink-0"
                                  />

                                  {/* Doctor & Appointment Details */}
                                  <div>
                                    <p className="text-sm font-bold text-slate-900 leading-tight">
                                      {formatDoctorName(appt.doctor_name || "Dr. Priya Sharma")}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {appt.doctor_specialization || "General Physician"}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                      Room {appt.room_number || "102"} • {appt.visit_type || "OPD"}
                                    </p>
                                  </div>
                                </div>

                                {/* Status, Time, Token & Details Action */}
                                <div className="flex items-center gap-4 self-end sm:self-center">
                                  <div className="text-right">
                                    <span
                                      className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                                        isUpcoming
                                          ? "bg-sky-50 text-sky-700 border border-sky-100"
                                          : appt.status === "cancelled"
                                          ? "bg-rose-50 text-rose-700 border border-rose-100"
                                          : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                      }`}
                                    >
                                      {isUpcoming ? "Upcoming" : appt.status}
                                    </span>
                                    <p className="text-xs text-slate-600 font-medium mt-1">
                                      {appt.slot_time ? (appt.slot_time.includes("-") ? appt.slot_time : `${appt.slot_time} - ${appt.slot_time.split(":")[0]}:30`) : "10:30 AM - 11:00 AM"}
                                    </p>
                                    <p className="text-[11px] text-slate-500 font-medium">
                                      Token #{appt.token_number || "3"}
                                    </p>
                                  </div>

                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setViewApptDetails(appt)}
                                    className="text-xs font-semibold text-slate-700 border-slate-200 hover:text-[#0c7a5f] hover:border-emerald-300 rounded-lg px-3 py-1.5"
                                  >
                                    View Details &gt;
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-slate-400">
                    <CalendarCheck size={40} className="mx-auto mb-2 opacity-30 text-emerald-700" />
                    <p className="text-sm">No appointments scheduled</p>
                    <Button
                      onClick={() => navigate("/book")}
                      className="mt-4 bg-[#0c7a5f] hover:bg-[#0a664e] text-white text-xs font-semibold"
                    >
                      Book First Appointment
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* TAB CONTENT: PRESCRIPTIONS */}
            {activeTab === "prescriptions" && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-slate-900 font-sans">
                    My Medical Prescriptions
                  </h2>
                  <span className="text-xs text-slate-400">{prescriptions.length} Records</span>
                </div>

                {prescriptions.length > 0 ? (
                  <div className="space-y-3">
                    {prescriptions.map((rx) => (
                      <div
                        key={rx.id}
                        className="rounded-xl border border-slate-200/80 p-4 hover:border-emerald-300 transition-colors flex items-start justify-between gap-4"
                      >
                        <div className="flex items-start gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
                            <Pill size={18} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-[#0c7a5f] font-bold">{rx.rx_number || "RX-2026"}</span>
                              <span className="text-xs text-slate-400">• {rx.created_at?.split("T")[0]}</span>
                            </div>
                            <p className="text-sm font-bold text-slate-900 mt-0.5">{formatDoctorName(rx.doctor_name || "Dr. Priya Sharma")}</p>
                            <p className="text-xs text-slate-600 mt-0.5">Diagnosis: <span className="font-medium text-slate-800">{rx.diagnosis || "General checkup"}</span></p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {(rx.medicines || []).map((m, idx) => (
                                <span key={idx} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-medium">
                                  {m.name || m} {m.dosage ? `(${m.dosage})` : ""}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewPrescription(rx)}
                          className="text-xs text-[#0c7a5f] border-emerald-200 hover:bg-emerald-50 rounded-lg gap-1.5"
                        >
                          <FileText size={13} /> View Rx
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-slate-400">
                    <Pill size={40} className="mx-auto mb-2 opacity-30 text-emerald-700" />
                    <p className="text-sm">No active prescriptions on file</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB CONTENT: LAB REPORTS */}
            {activeTab === "lab" && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold text-slate-900 font-sans">
                    Lab & Diagnostic Reports
                  </h2>
                  <span className="text-xs text-slate-400">{labReports.length} Reports</span>
                </div>

                {labReports.length > 0 ? (
                  <div className="divide-y divide-slate-100 border border-slate-200/80 rounded-xl overflow-hidden">
                    {labReports.map((test) => (
                      <div key={test.id} className="p-4 flex items-center justify-between gap-4 hover:bg-slate-50/50 transition">
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-[#f5f3ff] text-[#7c3aed] flex items-center justify-center flex-shrink-0">
                            <FlaskConical size={18} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{test.test_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{test.test_type?.replace("_", " ")} • {test.created_at?.split("T")[0]}</p>
                            {test.result && (
                              <p className="text-xs text-emerald-700 font-medium mt-1 bg-emerald-50 px-2 py-0.5 rounded inline-block">
                                Result: {test.result}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize ${
                            test.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-amber-50 text-amber-700 border border-amber-100"
                          }`}>
                            {test.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-slate-400">
                    <FlaskConical size={40} className="mx-auto mb-2 opacity-30 text-purple-700" />
                    <p className="text-sm">No lab reports found</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB CONTENT: BILLING */}
            {activeTab === "bills" && (
              <BillingTabContent
                unpaidBills={bills.filter((b) => b.status !== "paid")}
                paidBills={bills.filter((b) => b.status === "paid")}
                payingBill={payingBill}
                payBill={payBill}
                downloadInvoice={downloadInvoice}
                totalSpent={totalSpent}
                navigate={navigate}
              />
            )}

          </div>

          {/* Bottom Footer Link */}
          <div className="border-t border-slate-100 px-6 py-3.5 bg-slate-50/40">
            <button
              onClick={() => handleTabChange("appointments")}
              className="text-xs font-bold text-[#0c7a5f] hover:text-[#095b46] flex items-center gap-1 transition"
            >
              View All Appointments &rarr;
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: Next Visit Card & Quick Actions (Matching Image 2) */}
        <div className="lg:col-span-4 space-y-5">
          {/* Next Visit Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs overflow-hidden">
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center flex-shrink-0">
                  <Calendar size={14} />
                </div>
                <h3 className="text-sm font-bold text-slate-900 font-sans tracking-tight">Next Visit</h3>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </div>

            {nextUpcoming ? (
              <div className="pt-4 space-y-4">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={getDoctorAvatar(nextUpcoming.doctor_name)}
                    alt={nextUpcoming.doctor_name}
                    className="w-12 h-12 min-w-[48px] max-w-[48px] rounded-full object-cover border-2 border-emerald-100 flex-shrink-0 shadow-2xs"
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-bold text-slate-900 leading-snug truncate">
                      {formatDoctorName(nextUpcoming.doctor_name || "Dr. Priya Sharma")}
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5 truncate font-normal">
                      {nextUpcoming.doctor_specialization || "General Physician"}
                    </p>
                  </div>
                </div>

                <div className="space-y-2.5 text-xs text-slate-600 bg-[#f8faf9] rounded-xl p-3.5 border border-slate-100">
                  <div className="flex items-center gap-2 font-medium">
                    <Calendar size={14} className="text-[#0c7a5f] flex-shrink-0" />
                    <span className="truncate">
                      {nextUpcoming.visit_date?.split("T")[0] ? new Date(nextUpcoming.visit_date?.split("T")[0]).toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "Mon, 07 Sep 2026"} • {nextUpcoming.slot_time || "10:30 AM - 11:00 AM"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-0.5">
                    <div className="flex items-center gap-1.5 text-slate-500 min-w-0">
                      <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                      <span className="truncate">Room {nextUpcoming.room_number || "102"} • {nextUpcoming.visit_type || "OPD"}</span>
                    </div>
                    <span className="bg-[#eaf8f0] text-[#0c7a5f] font-bold text-[11px] px-2.5 py-0.5 rounded-full flex-shrink-0">
                      Token #{nextUpcoming.token_number || "3"}
                    </span>
                  </div>
                </div>

                {/* Primary & Secondary Action Buttons */}
                <div className="space-y-2 pt-1">
                  <Button
                    onClick={() => navigate("/book")}
                    className="w-full bg-[#0c7a5f] hover:bg-[#0a664e] text-white rounded-xl py-2.5 text-xs font-semibold flex items-center justify-center gap-2 shadow-xs transition"
                  >
                    <CalendarCheck size={15} />
                    <span>Book New Appointment</span>
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => handleTabChange("appointments")}
                    className="w-full border-[#0c7a5f] text-[#0c7a5f] hover:bg-[#eaf8f0] rounded-xl py-2 text-xs font-semibold flex items-center justify-center gap-2 transition"
                  >
                    <Calendar size={14} />
                    <span>View All Appointments</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-slate-400 text-xs">
                No scheduled visit
              </div>
            )}
          </div>

          {/* Quick Actions Card (Matching Image 2) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <div className="flex items-center gap-2 pb-3.5 border-b border-slate-100">
              <Zap size={16} className="text-emerald-700" />
              <h3 className="text-sm font-bold text-slate-900 font-sans">Quick Actions</h3>
            </div>

            {/* 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 pt-3.5">
              <button
                onClick={() => navigate("/book")}
                className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-100 hover:border-emerald-200 bg-[#fbfdfc] hover:bg-[#f0f9f4] transition text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#eaf8f0] text-[#0c7a5f] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition">
                  <CalendarCheck size={16} />
                </div>
                <span className="text-xs font-semibold text-slate-800 leading-tight">Book Appointment</span>
              </button>

              <button
                onClick={() => handleTabChange("prescriptions")}
                className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-100 hover:border-blue-200 bg-[#fbfdfc] hover:bg-[#f0f7ff] transition text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#ebf4ff] text-[#2563eb] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition">
                  <Pill size={16} />
                </div>
                <span className="text-xs font-semibold text-slate-800 leading-tight">View Prescriptions</span>
              </button>

              <button
                onClick={() => handleTabChange("lab")}
                className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-100 hover:border-cyan-200 bg-[#fbfdfc] hover:bg-[#ecfeff] transition text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#ecfeff] text-[#0891b2] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition">
                  <FlaskConical size={16} />
                </div>
                <span className="text-xs font-semibold text-slate-800 leading-tight">View Lab Reports</span>
              </button>

              <button
                onClick={() => handleTabChange("bills")}
                className="flex items-center gap-2.5 p-3 rounded-xl border border-slate-100 hover:border-purple-200 bg-[#fbfdfc] hover:bg-[#faf5ff] transition text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#faf5ff] text-[#9333ea] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition">
                  <CreditCard size={16} />
                </div>
                <span className="text-xs font-semibold text-slate-800 leading-tight">Make Payment</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* APPOINTMENT DETAILS MODAL */}
      <Dialog open={!!viewApptDetails} onOpenChange={() => setViewApptDetails(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold font-sans">Appointment Details</DialogTitle>
          </DialogHeader>
          {viewApptDetails && (
            <div className="space-y-4 pt-2 text-sm">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <img
                  src={getDoctorAvatar(viewApptDetails.doctor_name)}
                  alt="Doctor"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-slate-900">{formatDoctorName(viewApptDetails.doctor_name)}</p>
                  <p className="text-xs text-slate-500">{viewApptDetails.doctor_specialization || "General Physician"}</p>
                  <p className="text-xs text-slate-400">Room {viewApptDetails.room_number || "102"} • {viewApptDetails.visit_type || "OPD"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-400 block">Date</span>
                  <span className="font-semibold text-slate-800">{viewApptDetails.visit_date?.split("T")[0]}</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-400 block">Slot Time</span>
                  <span className="font-semibold text-slate-800">{viewApptDetails.slot_time || "10:30 AM"}</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-400 block">Token Number</span>
                  <span className="font-bold text-[#0c7a5f]">#{viewApptDetails.token_number}</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-400 block">Status</span>
                  <span className="font-semibold capitalize text-slate-800">{viewApptDetails.status}</span>
                </div>
              </div>

              {viewApptDetails.complaint && (
                <div className="p-3 bg-slate-50 rounded-lg text-xs">
                  <span className="text-slate-400 block mb-0.5">Notes / Complaint</span>
                  <p className="text-slate-700">{viewApptDetails.complaint}</p>
                </div>
              )}

              {/* Action options */}
              {(viewApptDetails.status === "waiting" || viewApptDetails.status === "upcoming" || viewApptDetails.status === "in_consultation") && (
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-xs border-slate-200"
                    onClick={() => openReschedule(viewApptDetails)}
                  >
                    Reschedule
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 text-xs"
                    onClick={() => cancelAppointment(viewApptDetails.id)}
                  >
                    Cancel Visit
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RESCHEDULE MODAL */}
      <Dialog open={!!rescheduleAppt} onOpenChange={() => setRescheduleAppt(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-sans text-base">Reschedule Appointment</DialogTitle>
          </DialogHeader>
          {rescheduleAppt && (
            <div className="space-y-4 mt-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs">
                <p className="text-slate-400">Doctor</p>
                <p className="font-bold text-slate-900 text-sm mt-0.5">{formatDoctorName(rescheduleAppt.doctor_name)}</p>
                <p className="text-slate-500 mt-0.5">Current: {rescheduleAppt.visit_date?.split("T")[0]} at {rescheduleAppt.slot_time}</p>
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Select New Date</Label>
                <Input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="mt-1 text-xs border-slate-200"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div>
                <Label className="text-xs text-slate-700 font-semibold">Available Time Slots</Label>
                {loadingSlots ? (
                  <p className="text-xs text-slate-400 mt-2">Checking slots...</p>
                ) : !availableSlots?.slots?.length ? (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500 mt-1">
                    No available slots for this date.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                    {availableSlots.slots.map((slot, idx) => (
                      <button
                        key={idx}
                        type="button"
                        disabled={slot.is_full}
                        onClick={() => setSelectedSlot(slot.start)}
                        className={`rounded-xl border p-2 text-left text-xs transition ${
                          slot.is_full
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            : selectedSlot === slot.start
                            ? "border-[#0c7a5f] bg-[#eaf8f0] text-[#0c7a5f] font-bold"
                            : "border-slate-200 bg-white hover:border-emerald-300"
                        }`}
                      >
                        <div className="font-semibold">{slot.start}</div>
                        <div className="text-[10px] text-slate-400">{slot.is_full ? "Full" : "Available"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 text-xs" onClick={() => setRescheduleAppt(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={rescheduleSaving || !selectedSlot}
                  className="flex-1 text-xs bg-[#0c7a5f] hover:bg-[#0a664e] text-white"
                  onClick={handleReschedule}
                >
                  {rescheduleSaving ? "Rescheduling..." : "Confirm Reschedule"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* VIEW PRESCRIPTION MODAL */}
      <Dialog open={!!viewPrescription} onOpenChange={() => setViewPrescription(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-sans text-base">Prescription Details</DialogTitle>
          </DialogHeader>
          {viewPrescription && (
            <div className="space-y-4 pt-2 text-xs">
              <div className="flex justify-between border-b pb-3">
                <div>
                  <p className="font-mono text-sm font-bold text-[#0c7a5f]">{viewPrescription.rx_number}</p>
                  <p className="text-slate-500 mt-0.5">Date: {viewPrescription.created_at?.split("T")[0]}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900">{formatDoctorName(viewPrescription.doctor_name)}</p>
                  <p className="text-slate-500">Diagnosis: {viewPrescription.diagnosis}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-bold text-slate-800 text-xs uppercase tracking-wider">Prescribed Medicines</p>
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
                  {(viewPrescription.medicines || []).map((m, idx) => (
                    <div key={idx} className="p-2.5 bg-slate-50 flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-slate-800">{m.name}</p>
                        <p className="text-slate-500 text-[11px]">{m.dosage} • {m.frequency || "Once daily"} • {m.duration || "5 days"}</p>
                      </div>
                      {m.instructions && (
                        <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-600">
                          {m.instructions}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {viewPrescription.notes && (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                  <span className="font-semibold text-[#0c7a5f] block mb-0.5">Doctor's Advice</span>
                  <p className="text-slate-700">{viewPrescription.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
